import React, { useContext, useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import { useQuery, useMutation, NetworkStatus } from "@apollo/client";
import {
  Breadcrumb,
  Dimmer,
  Dropdown,
  Icon,
  Message,
  Loader,
  List,
  Segment,
  Button,
  Menu,
  Header,
} from "semantic-ui-react";
import {
  GET_STUDENT_REPORT,
  MARK_REPORT_AS_READ,
  ON_COMMENT_ADDED_FOR_REPORT,
} from "../GraphQL";
import { getReportTitle } from "../utils";
import UserContext from "contexts/UserContext";
import ReportActivity from "components/ReportActivity";
import Comments from "components/Comments";
import Attachment from "components/Attachment";
import { languageOptions, _t } from "../l10n";

const ViewReportPage = ({ match, accessReason }) => {
  const user = useContext(UserContext);
  const { studentId, reportId } = match.params;
  const [activeMenuItem, setActiveMenuItem] = useState("comments");
  const [isTranslated, setIsTranslated] = useState(false);
  const {
    error,
    loading,
    data,
    previousData,
    refetch,
    networkStatus,
    subscribeToMore,
  } = useQuery(GET_STUDENT_REPORT, {
    variables: { studentId, reportId, accessReason, language: "" },
    notifyOnNetworkStatusChange: true,
  });
  const [markReportAsRead] = useMutation(MARK_REPORT_AS_READ);

  useEffect(() => {
    console.log("subscribeToMore");
    subscribeToMore({
      document: ON_COMMENT_ADDED_FOR_REPORT,
      variables: { report: reportId },
      onError: console.log,
      updateQuery: (prev, { subscriptionData }) => {
        console.log("new comment", subscriptionData);
        if (!subscriptionData.data) return prev;
        const newComment = subscriptionData.data.commentAddedForReport;
        console.log("previous", prev);
        const prevReport = prev.getStudent.report;
        const newComments = [...(prevReport.comments || []), newComment];

        const retVal = JSON.parse(JSON.stringify(prev));
        retVal.getStudent.report.comments = newComments;
        return retVal;
      },
    });
    // eslint-disable-next-line
  }, []);

  useEffect(
    () => {
      if (data) {
        markReportAsRead({ variables: { report: reportId } });
      }
    },
    // eslint-disable-next-line
    [data],
  );

  if (
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables
  ) {
    //TODO
  } else if (loading) {
    return <Loader active />;
  }
  if (error) {
    throw error;
  }

  const student = (data || previousData).getStudent;

  if (!student.report) {
    return (
      <Message negative>
        <Message.Header>Report not found.</Message.Header>
        <p>This report no longer exists.</p>
      </Message>
    );
  }

  const { name, myTeacher, report } = student;

  const Items = report.fields.map((f, i) => {
    const itemProps = {
      header: f.label,
      description:
        f.control === "Checkbox" && f.value
          ? JSON.parse(f.value).join(", ")
          : f.value,
    };

    return itemProps.description ? (
      <List.Item key={`item${i}`}>
        <Dimmer active={networkStatus !== NetworkStatus.ready} inverted>
          <Loader />
        </Dimmer>
        <List.Header>{itemProps.header}</List.Header>
        <List.Description>{itemProps.description}</List.Description>
      </List.Item>
    ) : null;
  });

  const getCommentContent = () => {
    let commentContent = (
      <div>
        <i>Comments not available for this report. </i>
      </div>
    );
    if (
      myTeacher.isProUser ||
      (report.comments && report.comments.length > 0)
    ) {
      commentContent = report.sent ? (
        <Comments
          report={report}
          student={student}
          teacherIsPro={myTeacher.isProUser}
        />
      ) : (
        <Header
          as="h3"
          content="Comments"
          subheader="Comments disabled until report is sent."
        />
      );
    } else {
      if (user.isTeacher && !user.isPro()) {
        commentContent = (
          <div>
            Get access to report comments with Know My Day Pro.{" "}
            <Link to="/upgrade">Upgrade to Pro for free!</Link>
          </div>
        );
      }
    }
    return commentContent;
  };

  return (
    <div>
      <Breadcrumb size="tiny">
        <Breadcrumb.Section>
          <Link to="/">Home</Link>
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section>
          <Link to={`/students/${student.id}`}>{student.name}</Link>
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Report</Breadcrumb.Section>
      </Breadcrumb>

      <Segment.Group raised className="report-container">
        <Message attached>
          <Button
            className="print-button"
            floated="right"
            icon="print"
            onClick={() => window.print()}
          />
          {myTeacher.isProUser ? (
            <div>
              {isTranslated ? (
                <Button
                  className="LanguageDropdown"
                  onClick={async () => {
                    await refetch({ language: "" });
                    setIsTranslated(false);
                  }}
                >
                  <Icon name="world" /> {_t("show_original")}
                </Button>
              ) : (
                <Dropdown
                  button
                  icon="world"
                  labeled
                  text={_t("translate")}
                  className="icon LanguageDropdown"
                  onChange={async (_e, { value }) => {
                    console.log("refetching", value);
                    await refetch({ language: value });
                    setIsTranslated(true);
                  }}
                  options={languageOptions}
                  placeholder="Choose an option"
                  selection
                />
              )}
            </div>
          ) : null}
          <Message.Header>{name}</Message.Header>
          {getReportTitle(report)}
        </Message>
        <Segment attached>
          <List divided relaxed>
            {Items}
          </List>
        </Segment>
        {report.files && report.files.length > 0 ? (
          <Segment attached>
            <List divided relaxed>
              <List.Item>
                <List.Header>
                  <Icon name="attach" />
                  Attachments
                </List.Header>
                <List>
                  {report.files.map((file) => (
                    <Attachment key={file.key} file={file} />
                  ))}
                </List>
              </List.Item>
            </List>
          </Segment>
        ) : null}
      </Segment.Group>

      <div className="report-menu">
        <Menu secondary pointing>
          <Menu.Item
            name="Comments"
            className="comments-menu-item"
            active={activeMenuItem === "comments"}
            onClick={() => setActiveMenuItem("comments")}
          />
          {user.isTeacher ? (
            <Menu.Item
              name="Activity"
              className="activity-menu-item"
              active={activeMenuItem === "activity"}
              onClick={() => setActiveMenuItem("activity")}
            />
          ) : null}
        </Menu>
        <Segment className="menu-content">
          {activeMenuItem === "comments" ? getCommentContent() : null}
          {activeMenuItem === "activity" ? (
            <ReportActivity studentId={studentId} reportId={reportId} />
          ) : null}
        </Segment>
      </div>
    </div>
  );
};

export default withRouter(ViewReportPage);
